import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Link as LinkIcon, 
  FileText, 
  Code,
  Wrench,
  ExternalLink
} from 'lucide-react';

// 工具分类和工具列表
const tools = [
  {
    category: '文本工具',
    icon: FileText,
    items: [
      {
        id: 'url-extractor',
        name: '网址提取工具',
        description: '从文本中提取所有的网址链接',
        path: '/tools/url-extractor'
      },
      {
        id: 'text-cleaner',
        name: '文本去重工具',
        description: '自动删除文本中的重复行',
        path: '/tools/text-cleaner'
      },
      {
        id: 'empty-line-remover',
        name: '空行删除工具',
        description: '自动删除文本中的空白行',
        path: '/tools/empty-line-remover'
      }
    ]
  },
  {
    category: '网盘工具',
    icon: LinkIcon,
    items: [
      {
        id: 'share-link-parser',
        name: '分享链接整理',
        description: '整理各类网盘分享链接和标题',
        path: '/tools/share-link-parser'
      },
      {
        id: 'quark-link-formatter',
        name: '夸克网盘格式化',
        description: '处理夸克网盘分享链接的标题和地址格式',
        path: '/tools/quark-link-formatter'
      }
    ]
  },
  {
    category: '编程工具',
    icon: Code,
    items: [
      {
        id: 'markdown-converter',
        name: 'Markdown 转换器',
        description: '将 Markdown 文本转换为 HTML 格式',
        path: '/tools/markdown-converter'
      },
      {
        id: 'html-editor',
        name: 'HTML 在线编辑器',
        description: '在线编辑 HTML 代码并实时预览效果',
        path: '/tools/html-editor'
      }
    ]
  }
];

export default function Tools() {
  const [searchTerm, setSearchTerm] = useState('');

  // 搜索过滤功能
  const filteredTools = tools.map(category => ({
    ...category,
    items: category.items.filter(tool => 
      tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.items.length > 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航 */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Wrench className="w-8 h-8 text-indigo-600" />
              <h1 className="ml-2 text-xl font-bold text-gray-900">
                一雄在线工具箱
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-gray-500 hover:text-gray-700">
                首页
              </Link>
              <Link to="/about" className="text-gray-500 hover:text-gray-700">
                关于
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 搜索框 */}
        <div className="max-w-3xl mx-auto mb-8">
          <input
            type="text"
            placeholder="搜索工具..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        {/* 工具分类列表 */}
        <div className="space-y-12">
          {filteredTools.map((category) => (
            <div key={category.category}>
              <div className="flex items-center mb-6">
                <category.icon className="w-6 h-6 text-indigo-600" />
                <h2 className="ml-2 text-2xl font-bold text-gray-900">
                  {category.category}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.items.map((tool) => (
                  <Link
                    key={tool.id}
                    to={tool.path}
                    className="group block p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600">
                        {tool.name}
                      </h3>
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-indigo-600" />
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      {tool.description}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* 页脚 */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-500 text-sm">
            <p>© {new Date().getFullYear()} 一雄在线工具箱. All rights reserved.</p>
            <p className="mt-2">
              便捷实用的在线工具集合，无需下载安装，提高工作效率
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}